import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import Branding from './Branding';

// Register Chart.js components for radar charts
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const CuriosityRadarChart = ({ userId, userTier, hasFeatureAccess }) => {
  const [curiosityData, setCuriosityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCuriosityData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/analytics/curiosity-trends/${userId}`);
        if (response.data.success && response.data.curiosity_evolution.length > 0) {
          // Get the latest curiosity state
          const latestCuriosity = response.data.curiosity_evolution[0];
          setCuriosityData(latestCuriosity);
        }
      } catch (err) {
        console.error('Error fetching curiosity data:', err);
        setError('Failed to load curiosity metrics');
      } finally {
        setLoading(false);
      }
    };

    if (hasFeatureAccess('CURIOSITY_METRICS')) {
      fetchCuriosityData();
    }
  }, [userId, hasFeatureAccess]);

  const chartData = curiosityData ? {
    labels: [
      'Exploration Tendency',
      'Persistence',
      'Question Frequency',
      'Knowledge Gap Awareness'
    ],
    datasets: [{
      label: 'Curiosity Profile',
      data: [
        curiosityData.exploration_tendency || 0,
        curiosityData.persistence || 0,
        curiosityData.question_frequency || 0,
        curiosityData.knowledge_gap_awareness || 0
      ],
      backgroundColor: `${Branding.colors.deepPurple}20`,
      borderColor: Branding.colors.deepPurple,
      borderWidth: 2,
      pointBackgroundColor: Branding.colors.deepPurple,
      pointBorderColor: Branding.colors.pureWhite,
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: Branding.colors.darkSlate,
        titleColor: Branding.colors.pureWhite,
        bodyColor: Branding.colors.pureWhite,
        borderColor: Branding.colors.deepPurple,
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: {
          family: Branding.typography.fontFamily,
          size: 14,
          weight: 'semibold'
        },
        bodyFont: {
          family: Branding.typography.fontFamily,
          size: 12
        },
        callbacks: {
          label: function(context) {
            return `${context.label}: ${Math.round(context.parsed.r)}`;
          }
        }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        angleLines: {
          color: Branding.colors.veryLightGray,
          lineWidth: 1
        },
        grid: {
          color: Branding.colors.veryLightGray,
          lineWidth: 1
        },
        pointLabels: {
          font: {
            family: Branding.typography.fontFamily,
            size: 11,
            weight: 'medium'
          },
          color: Branding.colors.darkSlate,
          padding: 8
        },
        ticks: {
          display: false,
          stepSize: 25
        }
      }
    },
    elements: {
      line: {
        tension: 0.2
      }
    }
  };

  const getCuriosityInsight = (data) => {
    if (!data) return null;

    const values = [
      data.exploration_tendency,
      data.persistence,
      data.question_frequency,
      data.knowledge_gap_awareness
    ];
    
    const maxValue = Math.max(...values);
    const dominantTrait = [
      'exploration_tendency',
      'persistence', 
      'question_frequency',
      'knowledge_gap_awareness'
    ][values.indexOf(maxValue)];

    const insights = {
      exploration_tendency: "High exploration drive - loves discovering new concepts",
      persistence: "Strong persistence - doesn't give up when facing challenges", 
      question_frequency: "Active questioner - engages deeply with learning material",
      knowledge_gap_awareness: "Self-aware learner - recognizes areas for improvement"
    };

    return {
      trait: dominantTrait.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      insight: insights[dominantTrait],
      strength: Math.round(maxValue)
    };
  };

  if (!hasFeatureAccess('CURIOSITY_METRICS')) {
    return (
      <div style={{
        ...Branding.components.analytics.dashboard.container,
        textAlign: 'center',
        padding: Branding.spacing.xl
      }}>
        <h3 style={{
          fontSize: Branding.typography.fontSize.lg,
          fontWeight: Branding.typography.fontWeight.semibold,
          color: Branding.colors.darkSlate,
          marginBottom: Branding.spacing.md
        }}>
          Curiosity Analytics
        </h3>
        <div style={{
          width: '72px',
          height: '72px',
          margin: '0 auto',
          marginBottom: Branding.spacing.md,
          background: `linear-gradient(135deg, ${Branding.colors.deepPurple}, ${Branding.colors.vibrantOrange})`,
          borderRadius: Branding.borders.radius.xl,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.3
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            background: Branding.colors.pureWhite,
            borderRadius: '50%'
          }} />
        </div>
        <p style={{
          color: Branding.colors.slateGray,
          marginBottom: Branding.spacing.lg
        }}>
          4-dimensional curiosity tracking and analysis
        </p>
        <div style={{
          backgroundColor: Branding.colors.vibrantOrange,
          color: Branding.colors.pureWhite,
          padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
          borderRadius: Branding.borders.radius.md,
          display: 'inline-block',
          fontSize: Branding.typography.fontSize.sm,
          fontWeight: Branding.typography.fontWeight.medium
        }}>
          Upgrade to Unlimited to unlock
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={Branding.components.analytics.dashboard.container}>
        <h3 style={{
          fontSize: Branding.typography.fontSize.lg,
          fontWeight: Branding.typography.fontWeight.semibold,
          color: Branding.colors.darkSlate,
          marginBottom: Branding.spacing.md
        }}>
          Curiosity Analytics
        </h3>
        <div style={{ textAlign: 'center', padding: Branding.spacing.xl }}>
          <div style={{
            color: Branding.colors.slateGray,
            fontSize: Branding.typography.fontSize.sm
          }}>
            Loading curiosity metrics...
          </div>
        </div>
      </div>
    );
  }

  if (error || !curiosityData) {
    return (
      <div style={Branding.components.analytics.dashboard.container}>
        <h3 style={{
          fontSize: Branding.typography.fontSize.lg,
          fontWeight: Branding.typography.fontWeight.semibold,
          color: Branding.colors.darkSlate,
          marginBottom: Branding.spacing.md
        }}>
          Curiosity Analytics
        </h3>
        <div style={{
          textAlign: 'center',
          padding: Branding.spacing.xl,
          backgroundColor: Branding.colors.offWhite,
          borderRadius: Branding.borders.radius.md
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            margin: '0 auto',
            marginBottom: Branding.spacing.md,
            background: `linear-gradient(135deg, ${Branding.colors.deepPurple}, ${Branding.colors.vibrantOrange})`,
            borderRadius: Branding.borders.radius.lg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.6
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              background: Branding.colors.pureWhite,
              borderRadius: '50%'
            }} />
          </div>
          <p style={{
            color: Branding.colors.slateGray,
            fontWeight: Branding.typography.fontWeight.medium
          }}>
            {error || 'No curiosity data available yet'}
          </p>
          <p style={{
            fontSize: Branding.typography.fontSize.sm,
            color: Branding.colors.slateGray,
            marginTop: Branding.spacing.sm
          }}>
            Complete a session with Mira to generate curiosity analytics
          </p>
        </div>
      </div>
    );
  }

  const insight = getCuriosityInsight(curiosityData);

  return (
    <div style={Branding.components.analytics.dashboard.container}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: Branding.spacing.lg
      }}>
        <h3 style={{
          fontSize: Branding.typography.fontSize.lg,
          fontWeight: Branding.typography.fontWeight.semibold,
          color: Branding.colors.darkSlate,
          margin: 0
        }}>
          Curiosity Analytics
        </h3>
        <div style={{
          backgroundColor: Branding.colors.deepPurple,
          color: Branding.colors.pureWhite,
          padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
          borderRadius: Branding.borders.radius.full,
          fontSize: Branding.typography.fontSize.xs,
          fontWeight: Branding.typography.fontWeight.medium
        }}>
          PREMIUM
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: Branding.spacing.lg,
        alignItems: 'center'
      }}>
        {/* Radar Chart */}
        <div style={{ 
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Radar data={chartData} options={chartOptions} />
        </div>
        
        {/* Insights Panel */}
        <div>
          <div style={{
            padding: Branding.spacing.lg,
            backgroundColor: Branding.colors.offWhite,
            borderRadius: Branding.borders.radius.md,
            border: `1px solid ${Branding.colors.veryLightGray}`
          }}>
            <h4 style={{
              fontSize: Branding.typography.fontSize.md,
              fontWeight: Branding.typography.fontWeight.semibold,
              color: Branding.colors.darkSlate,
              marginBottom: Branding.spacing.md,
              margin: 0
            }}>
              Curiosity Profile Insights
            </h4>
            
            {insight && (
              <div style={{
                padding: Branding.spacing.md,
                backgroundColor: Branding.colors.pureWhite,
                borderRadius: Branding.borders.radius.sm,
                border: `1px solid ${Branding.colors.deepPurple}30`,
                marginBottom: Branding.spacing.md
              }}>
                <div style={{
                  fontSize: Branding.typography.fontSize.sm,
                  fontWeight: Branding.typography.fontWeight.semibold,
                  color: Branding.colors.deepPurple,
                  marginBottom: Branding.spacing.xs
                }}>
                  Dominant Trait: {insight.trait} ({insight.strength}%)
                </div>
                <div style={{
                  fontSize: Branding.typography.fontSize.sm,
                  color: Branding.colors.darkSlate,
                  lineHeight: 1.4
                }}>
                  {insight.insight}
                </div>
              </div>
            )}
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: Branding.spacing.sm,
              fontSize: Branding.typography.fontSize.xs
            }}>
              {Object.entries(curiosityData).filter(([key]) => 
                ['exploration_tendency', 'persistence', 'question_frequency', 'knowledge_gap_awareness'].includes(key)
              ).map(([metric, value]) => (
                <div key={metric} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: Branding.spacing.xs,
                  backgroundColor: Branding.colors.nearlyWhite,
                  borderRadius: Branding.borders.radius.sm
                }}>
                  <span style={{ 
                    color: Branding.colors.slateGray,
                    textTransform: 'capitalize'
                  }}>
                    {metric.replace(/_/g, ' ')}:
                  </span>
                  <span style={{ 
                    color: Branding.colors.deepPurple,
                    fontWeight: Branding.typography.fontWeight.semibold
                  }}>
                    {Math.round(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Learning recommendations */}
          <div style={{
            marginTop: Branding.spacing.md,
            padding: Branding.spacing.md,
            backgroundColor: `${Branding.colors.tealBlue}10`,
            borderRadius: Branding.borders.radius.sm,
            border: `1px solid ${Branding.colors.tealBlue}30`
          }}>
            <div style={{
              fontSize: Branding.typography.fontSize.xs,
              fontWeight: Branding.typography.fontWeight.semibold,
              color: Branding.colors.tealBlue,
              marginBottom: Branding.spacing.xs
            }}>
              💡 Learning Recommendation
            </div>
            <div style={{
              fontSize: Branding.typography.fontSize.xs,
              color: Branding.colors.darkSlate,
              lineHeight: 1.3
            }}>
              {insight && insight.trait.includes('Exploration') && 
                "Try tackling more open-ended problems to leverage your natural curiosity drive."}
              {insight && insight.trait.includes('Persistence') && 
                "Challenge yourself with harder problems - your persistence will help you succeed."}
              {insight && insight.trait.includes('Question') && 
                "Keep asking great questions! Consider exploring related topics that interest you."}
              {insight && insight.trait.includes('Knowledge Gap') && 
                "Your self-awareness is a strength. Focus on areas you've identified for improvement."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CuriosityRadarChart;